import { FirebaseError } from 'firebase/app';
import { signInWithPopup, signInWithRedirect, type Auth, type AuthProvider } from 'firebase/auth';

/**
 * error code ที่แปลว่า "popup ใช้ไม่ได้ในสภาพแวดล้อมนี้" ไม่ใช่ "ผู้ใช้ทำอะไรผิด"
 *
 * `auth/internal-error` รวมถึงกรณีที่ Auth emulator relay event กลับมาไม่ได้
 * (`Auth Emulator Internal Error: No matching frame`) ซึ่งเกิดได้จากหลายสาเหตุ
 * ทั้ง popup ที่ไม่มี opener, ส่วนขยายเบราว์เซอร์ที่ไปยุ่งกับ iframe, หรือการตั้งค่าความเป็นส่วนตัว
 */
const FALLBACK_TO_REDIRECT_CODES = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/internal-error',
  'auth/web-storage-unsupported',
  'auth/timeout',
]);

/** ผู้ใช้กดปิด popup เอง — ไม่ใช่ความผิดพลาด ไม่ต้องทำอะไรต่อ */
const USER_CANCELLED_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

export function isUserCancelled(error: unknown): boolean {
  return error instanceof FirebaseError && USER_CANCELLED_CODES.has(error.code);
}

export function shouldFallbackToRedirect(error: unknown): boolean {
  return error instanceof FirebaseError && FALLBACK_TO_REDIRECT_CODES.has(error.code);
}

export type SignInOutcome =
  /** login สำเร็จผ่าน popup */
  | { status: 'signed-in' }
  /** กำลังพาไปหน้า Google — เบราว์เซอร์จะออกจากหน้านี้ ไม่ต้อง render อะไรต่อ */
  | { status: 'redirecting' }
  /** ผู้ใช้กดปิด popup เอง */
  | { status: 'cancelled' }
  | { status: 'failed'; error: unknown };

/**
 * เข้าสู่ระบบ โดยใช้ popup ก่อน แล้วถอยไป redirect ถ้า popup ใช้ไม่ได้
 *
 * ทำไมต้องมีสองทาง: popup ให้ประสบการณ์ดีกว่าเพราะไม่ต้องออกจากหน้าเดิม
 * แต่มันพังได้หลายแบบที่เราคุมไม่ได้ — เบราว์เซอร์บล็อก, โหมดส่วนตัว,
 * webview ในแอปอื่น, หรือ Auth emulator relay ไม่สำเร็จ
 * redirect ไม่ใช้ iframe relay เลยจึงทำงานได้ในที่ที่ popup ไปไม่รอด
 */
export async function signInWithFallback(
  auth: Auth,
  provider: AuthProvider,
): Promise<SignInOutcome> {
  try {
    await signInWithPopup(auth, provider);
    return { status: 'signed-in' };
  } catch (error) {
    if (isUserCancelled(error)) {
      return { status: 'cancelled' };
    }

    if (shouldFallbackToRedirect(error)) {
      try {
        // เรียกแล้วเบราว์เซอร์จะออกจากหน้านี้ทันที โค้ดหลังบรรทัดนี้จะไม่ทำงานตามปกติ
        await signInWithRedirect(auth, provider);
        return { status: 'redirecting' };
      } catch (redirectError) {
        return { status: 'failed', error: redirectError };
      }
    }

    return { status: 'failed', error };
  }
}
